import { type IconType } from 'react-icons';
import {
  GiArtificialIntelligence,
  GiChart,
  GiCircuitry,
  GiCog,
  GiCompass,
  GiCube,
  GiFeather,
  GiMegaphone,
  GiNetworkBars,
  GiNotebook,
  GiPaintBrush,
  GiRocket,
} from 'react-icons/gi';

import {
  FALLBACK_VIEWPORT_HEIGHT,
  FRONT_CARD_SCALE,
  FRONT_CARD_Y_OFFSET,
  MIN_STACK_SCALE,
  STACK_BASE_OFFSET_DESKTOP,
  STACK_BASE_OFFSET_MOBILE,
  STACK_GAPS_DESKTOP,
  STACK_GAPS_MOBILE,
  STACK_SCALE_FACTOR,
  STACK_VISIBLE_COUNT,
} from './constants';

const TITLE_ICON_MATCHERS: Array<{ Icon: IconType; pattern: RegExp }> = [
  { pattern: /(brand|identity|story|collateral|enablement)/i, Icon: GiPaintBrush },
  { pattern: /(ai|agent|automation|intelligence)/i, Icon: GiArtificialIntelligence },
  { pattern: /(data|analytics|insight|metric|intelligence)/i, Icon: GiChart },
  { pattern: /(platform|system|infrastructure|architecture|engine|automation)/i, Icon: GiCircuitry },
  { pattern: /(operations|workflow|logistics|scheduling|delivery|planning|status|detection)/i, Icon: GiCog },
  { pattern: /(component|library|design system|documentation|upgrade)/i, Icon: GiCube },
  { pattern: /(experience|editor|interface|ui|ux)/i, Icon: GiNotebook },
  { pattern: /(strategy|roadmap|navigation|operating model|playbook|compass)/i, Icon: GiCompass },
  { pattern: /(content|storytelling|copy|voice|narrative)/i, Icon: GiFeather },
  { pattern: /(governance|model|compliance)/i, Icon: GiNetworkBars },
  { pattern: /(launch|growth|acceleration|velocity|go-to-market)/i, Icon: GiRocket },
  { pattern: /(communication|marketing|sales|announcement)/i, Icon: GiMegaphone },
];

export const getTitleIcon = (label: string): IconType => {
  const normalized = label.toLowerCase();
  const matched = TITLE_ICON_MATCHERS.find(({ pattern }) => pattern.test(normalized));
  return matched?.Icon ?? GiCompass;
};

export const getViewportHeight = () =>
  typeof window !== 'undefined' ? window.innerHeight : FALLBACK_VIEWPORT_HEIGHT;

export const getStackScale = (relativePosition: number) => {
  if (relativePosition <= 0) return FRONT_CARD_SCALE;

  const clamped = Math.min(relativePosition, STACK_VISIBLE_COUNT - 1);
  const rawScale = FRONT_CARD_SCALE - clamped * STACK_SCALE_FACTOR;
  const adjustedScale = clamped === STACK_VISIBLE_COUNT - 1 ? rawScale * 0.9 : rawScale;

  return Math.max(adjustedScale, MIN_STACK_SCALE);
};

export const getStackYOffset = (relativePosition: number) => {
  if (relativePosition <= 0) return FRONT_CARD_Y_OFFSET;

  const clamped = Math.min(relativePosition, STACK_VISIBLE_COUNT - 1);
  let offset = FRONT_CARD_Y_OFFSET;
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  const gaps = isMobile ? STACK_GAPS_MOBILE : STACK_GAPS_DESKTOP;

  for (let i = 1; i <= clamped; i++) {
    const gap = gaps[i] ?? gaps[gaps.length - 1];
    offset -= gap;
  }

  return offset;
};

export const getStackBaseOffset = () => {
  if (typeof window === 'undefined') return STACK_BASE_OFFSET_DESKTOP;
  return window.innerWidth < 768 ? STACK_BASE_OFFSET_MOBILE : STACK_BASE_OFFSET_DESKTOP;
};
